import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { imgSrc, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Truck, RotateCcw } from "lucide-react";
import PageRenderer from "@/components/storefront/sections/PageRenderer";
import { usePage } from "@/lib/page";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const { add } = useCart();
  const { sections: tailSections } = usePage("_product_page");

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      const first = data.variants?.[0];
      if (first) { setSize(first.size); setColor(first.color); }
      setActiveImg(0);
    }).catch(() => {});
  }, [slug]);

  const sizes = useMemo(
    () => [...new Set((product?.variants || []).map((v) => v.size).filter(Boolean))],
    [product]
  );
  const colors = useMemo(() => {
    const map = new Map();
    (product?.variants || []).forEach((v) => { if (v.color && !map.has(v.color)) map.set(v.color, v.color_hex); });
    return Array.from(map, ([color, hex]) => ({ color, hex }));
  }, [product]);

  const activeVariant = useMemo(
    () => (product?.variants || []).find((v) => v.size === size && v.color === color),
    [product, size, color]
  );

  // Filter MAIN image by active color (fallback to all if none for that color),
  // but ALWAYS keep the full image set in the thumbnail strip so customers can
  // see every angle/colour at a glance — Shopify-style.
  const mainImages = useMemo(() => {
    if (!product?.images || product.images.length === 0) return [];
    if (!color) return product.images;
    const matched = product.images.filter((i) => !i.color || i.color === color);
    return matched.length > 0 ? matched : product.images;
  }, [product, color]);

  const allImages = useMemo(() => product?.images || [], [product]);

  // When color changes, reset to first matching image
  useEffect(() => {
    if (!product) return;
    const matchedIdx = (product.images || []).findIndex((i) => i.color === color);
    setActiveImg(matchedIdx >= 0 ? matchedIdx : 0);
  }, [color, product]);

  // Same-category recommendations
  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (!product?.category?.slug) return;
    api.get("/products", { params: { category: product.category.slug, limit: 4, exclude_id: product.id } })
       .then(({ data }) => setRelated(data));
  }, [product]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <div className="inline-block h-8 w-8 border-2 border-zinc-700 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const imgs = allImages.length ? allImages : [null];
  const mainImg = imgs[activeImg] || imgs[0];
  const price = activeVariant?.price_override ?? product.base_price;
  const oos = activeVariant ? activeVariant.stock <= 0 : true;

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="aspect-[4/5] border border-zinc-900 bg-zinc-900 relative overflow-hidden" data-testid="product-main-image">
              {mainImg ? (
                <img src={imgSrc(mainImg)} alt={product.name} className="w-full h-full object-cover transition-opacity duration-300" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700 font-heading uppercase tracking-widest text-xs">No Image</div>
              )}
            </div>
            {imgs.length > 1 && imgs[0] && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {imgs.map((im, i) => {
                  const dimmed = color && im?.color && im.color !== color;
                  return (
                    <button key={i} onClick={() => setActiveImg(i)}
                            className={`aspect-square border transition-all ${i === activeImg ? "border-[var(--theme-primary,#FF3B30)]" : "border-zinc-800 hover:border-zinc-600"} ${dimmed ? "opacity-40 hover:opacity-100" : ""}`}
                            title={im?.color || ""}
                            data-testid={`thumb-${i}`}>
                      {im && <img src={imgSrc(im)} alt="" className="w-full h-full object-cover" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            {product.category && (
              <div className="text-xs font-heading uppercase tracking-[0.35em] text-[var(--theme-primary,#FF3B30)] mb-3">{product.category.name}</div>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4">{product.name}</h1>
            <div className="flex items-baseline gap-4 mb-8">
              <span className="font-mono text-2xl">{formatPrice(price)}</span>
              {product.compare_price && product.compare_price > price && (
                <span className="font-mono text-sm text-zinc-600 line-through">{formatPrice(product.compare_price)}</span>
              )}
            </div>
            {product.description && <p className="text-zinc-400 leading-relaxed mb-10">{product.description}</p>}

            {sizes.length > 0 && (
              <div className="mb-6">
                <div className="font-heading uppercase tracking-widest text-[10px] text-zinc-400 mb-3">Size</div>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((s) => (
                    <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                            className={`px-4 py-2 border font-heading text-xs font-bold uppercase tracking-widest transition-all ${
                              size === s ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                            }`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {colors.length > 0 && (
              <div className="mb-8">
                <div className="font-heading uppercase tracking-widest text-[10px] text-zinc-400 mb-3">
                  Color {color && <span className="text-zinc-600 normal-case tracking-normal">— {color}</span>}
                </div>
                <div className="flex flex-wrap gap-3">
                  {colors.map((c) => (
                    <button key={c.color} data-testid={`color-${c.color}`} onClick={() => setColor(c.color)}
                            className={`h-9 w-9 border-2 transition-all ${color === c.color ? "border-white scale-110" : "border-zinc-700 hover:border-zinc-500"}`}
                            style={{ background: c.hex || "#333" }} title={c.color} />
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-zinc-500 mb-6">
              <span className={`h-2 w-2 ${oos ? "bg-zinc-600" : "bg-[#22C55E]"}`} />
              <span className="font-heading uppercase tracking-widest">{oos ? "Sold Out" : `In Stock (${activeVariant?.stock})`}</span>
            </div>

            <Button data-testid="add-to-cart-btn" disabled={!activeVariant || oos}
                    onClick={() => { add(activeVariant.id, product, activeVariant, 1); toast.success("Added to cart"); }}
                    className="w-full bg-[var(--theme-primary,#FF3B30)] hover:bg-[var(--theme-primary-hover,#D92D23)] disabled:bg-zinc-800 disabled:text-zinc-500 rounded-none font-heading font-bold uppercase tracking-widest py-6 text-sm">
              {oos ? "Sold Out" : "Add to Cart"}
            </Button>

            {/* Per-product policy snippets */}
            <div className="mt-10 space-y-3 pt-6 border-t border-zinc-900">
              <PolicyRow icon={Truck} label="Shipping" text={product.shipping_note || "Standard shipping rates apply."} link="/page/shipping-policy"/>
              <PolicyRow icon={RotateCcw} label="Returns" text={product.returns_note || "See our full returns policy."} link="/page/returns-policy"/>
            </div>
          </div>
        </div>
      </div>

      {/* Custom product page sections (story / featured / etc.) below the main info */}
      {tailSections && tailSections.length > 0 && (
        <PageRenderer sections={tailSections.map((s) => {
          if (s.section_type === "featured" && s.config?.category_slug === "_same_category") {
            return { ...s, _resolvedProducts: related };
          }
          return s;
        })} />
      )}
    </>
  );
}

function PolicyRow({ icon: Icon, label, text, link }) {
  return (
    <div className="flex items-start justify-between gap-4 text-xs">
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="h-4 w-4 text-zinc-500 flex-shrink-0 mt-0.5"/>
        <div className="min-w-0">
          <div className="font-heading uppercase tracking-widest text-zinc-300">{label}</div>
          <div className="text-zinc-500 mt-1 leading-relaxed">{text}</div>
        </div>
      </div>
      <Link to={link} className="text-[var(--theme-primary,#FF3B30)] hover:underline font-heading uppercase tracking-widest text-[10px] flex-shrink-0">Read more →</Link>
    </div>
  );
}
